import { DEFAULT_REFRESH_RATE } from "@/models/time-control/timeStateConsts";
import type { AnimationModeOpts, AutoRefreshModeOpts } from "@/models/time-control/timeStateCtxTypes";
import { type DateTime, Duration } from "luxon";

/** Common defaults used across modes */
export const DEFAULT_WINDOW = Duration.fromISO("P2D");
export const DEFAULT_TEMPORAL_RES = Duration.fromISO("PT1H");
export const DEFAULT_FPS = 2;

/**
 * Build a complete AutoRefresh options object (factory).
 * Ensures refreshRate/isLocked are always present.
 */
export function buildAutoRefreshModeOpts(partial?: Partial<AutoRefreshModeOpts>): AutoRefreshModeOpts {
  return {
    refreshRate: typeof partial?.refreshRate === "number" ? partial.refreshRate : DEFAULT_REFRESH_RATE,
    isLocked: !!partial?.isLocked,
  };
}

/**
 * Build a complete Animation ModeOpts (factory).
 * Takes a base time (usually displayTime), carries over refreshRate/isLocked if provided,
 * and fills all animation-specific fields.
 */
export function buildAnimationModeOpts(
  base: DateTime,
  common?: Partial<Pick<AnimationModeOpts, "refreshRate" | "isLocked">>,
  override?: Partial<Omit<AnimationModeOpts, "refreshRate" | "isLocked">>,
): AnimationModeOpts {
  const start = override?.startTime ?? base;
  const end = override?.endTime ?? start.plus(DEFAULT_WINDOW);

  return {
    refreshRate: typeof common?.refreshRate === "number" ? common.refreshRate : DEFAULT_REFRESH_RATE,
    isLocked: !!common?.isLocked,

    startTime: start,
    endTime: end,
    temporalResolution: override?.temporalResolution ?? DEFAULT_TEMPORAL_RES,
    fps: override?.fps ?? DEFAULT_FPS,
    isLooping: override?.isLooping ?? true,
    isPaused: override?.isPaused ?? true,
    animationTime_: override?.animationTime_ ?? start,
  };
}

/**
 * Effective refresh grid for snapping/alignment. Even if someone sets refreshRate=0 (meaning
 * "no periodic tick"), we still need a positive grid size for rounding → fall back to DEFAULT.
 */
export function getEffectiveRefreshRate(minutes: number | undefined): number {
  return Math.max(1, typeof minutes === "number" && minutes > 0 ? minutes : DEFAULT_REFRESH_RATE);
}
